import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { disciplinaryAPI, employeesAPI } from '../lib/api';
import { formatDate, getStatusColor, getCaseTypeColor, cn } from '../lib/utils';
import { Search, Plus, Scale, Filter, Eye, Edit } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const caseTypes = ['Verbal Warning', 'Written Warning', 'Final Warning', 'Hearing', 'Suspension'];

export default function Disciplinary() {
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [newCase, setNewCase] = useState({
    employee_id: '',
    case_type: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    outcome: '',
  });
  const navigate = useNavigate();
  const { isHRManager } = useAuth();

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const [casesRes, empRes] = await Promise.all([
        disciplinaryAPI.getAll({ status: filterStatus !== 'all' ? filterStatus : undefined }),
        employeesAPI.getAll({ status: 'Active' }),
      ]);
      setCases(casesRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      toast.error('Failed to load disciplinary cases');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async () => {
    if (!newCase.employee_id || !newCase.case_type || !newCase.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await disciplinaryAPI.create(newCase);
      toast.success('Disciplinary case created');
      setShowNewCaseModal(false);
      setNewCase({
        employee_id: '',
        case_type: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        outcome: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case');
    }
  };

  const handleUpdateCase = async (id, updates) => {
    try {
      await disciplinaryAPI.update(id, updates);
      toast.success('Case updated');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update case');
    }
  };

  const openCaseDetail = (caseData) => {
    setSelectedCase(caseData);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6 page-transition" data-testid="disciplinary-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
            <Scale className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            Disciplinary Cases
          </h1>
          <p className="text-muted-foreground mt-1">Manage employee disciplinary actions</p>
        </div>
        {isHRManager() && (
          <Button onClick={() => setShowNewCaseModal(true)} data-testid="new-case-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Case
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-48" data-testid="filter-case-status">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cases Table */}
      <div className="glass-card table-scroll">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Employee</TableHead>
              <TableHead>Case Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Loading cases...
                </TableCell>
              </TableRow>
            ) : cases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No disciplinary cases found
                </TableCell>
              </TableRow>
            ) : (
              cases.map((caseItem) => (
                <TableRow 
                  key={caseItem.id} 
                  className="table-row-hover cursor-pointer"
                  onClick={() => openCaseDetail(caseItem)}
                  data-testid={`case-row-${caseItem.id}`}
                >
                  <TableCell className="font-medium">{caseItem.employee_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("border", getCaseTypeColor(caseItem.case_type))}>
                      {caseItem.case_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground">
                    {caseItem.description}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(caseItem.date)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("border", getStatusColor(caseItem.status))}>
                      {caseItem.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => openCaseDetail(caseItem)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* New Case Modal */}
      <Dialog open={showNewCaseModal} onOpenChange={setShowNewCaseModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Disciplinary Case</DialogTitle>
            <DialogDescription>Create a new disciplinary case for an employee</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select 
                value={newCase.employee_id} 
                onValueChange={(v) => setNewCase({...newCase, employee_id: v})}
              >
                <SelectTrigger data-testid="select-case-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.full_name} - {emp.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Case Type *</Label>
              <Select 
                value={newCase.case_type} 
                onValueChange={(v) => setNewCase({...newCase, case_type: v})}
              >
                <SelectTrigger data-testid="select-case-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {caseTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newCase.date}
                onChange={(e) => setNewCase({...newCase, date: e.target.value})}
                data-testid="input-case-date"
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={newCase.description}
                onChange={(e) => setNewCase({...newCase, description: e.target.value})}
                placeholder="Describe the incident or reason for disciplinary action..."
                rows={4}
                data-testid="input-case-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCaseModal(false)}>Cancel</Button>
            <Button onClick={handleCreateCase} data-testid="create-case-btn">Create Case</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Case Details</DialogTitle>
          </DialogHeader>
          
          {selectedCase && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Employee</p>
                  <p className="font-medium">{selectedCase.employee_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(selectedCase.date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <Badge variant="outline" className={cn("border", getCaseTypeColor(selectedCase.case_type))}>
                    {selectedCase.case_type}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant="outline" className={cn("border", getStatusColor(selectedCase.status))}>
                    {selectedCase.status}
                  </Badge>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-1">Description</p>
                <p className="text-foreground">{selectedCase.description}</p>
              </div>

              {selectedCase.outcome && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Outcome</p>
                  <p className="text-foreground">{selectedCase.outcome}</p>
                </div>
              )}

              {isHRManager() && selectedCase.status === 'Open' && (
                <div className="pt-4 border-t border-border">
                  <Label>Update Outcome</Label>
                  <Textarea
                    placeholder="Enter case outcome..."
                    className="mt-2"
                    id="case-outcome"
                    data-testid="case-outcome-input"
                  />
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        const outcome = document.getElementById('case-outcome').value;
                        handleUpdateCase(selectedCase.id, { outcome, status: 'Closed' });
                      }}
                      data-testid="close-case-btn"
                    >
                      Close Case
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
